import {defineStore} from "pinia";
import {DonationService} from "../services/donation.service";
import {OrganizationService} from "../services/organization.service";
import {ApplicationHelper} from "../helpers/ApplicationHelper";
import {useAnalyticEventStore} from "./analyticEvents";

export const OfflineDonationTypes = ["Cash", "Check", "Online (legacy)", "DAF", "Other"];

export const useDonationStore = defineStore("donation", {
  state: () => ({
    donationService: null,
    organizationService: new OrganizationService(),
    donations: [],
    funds: [],
    organizationId: null,
    loading: true,
    currentOrganization: "null",
    showOfflineModal: false,
    perPage: 10,
    currentDonation: {},
    loadingDonation: false,
    donationError: false,
    givingBlind: false,
    targetsLoaded: false,
    donationTargets: [],
    // these are for analytic events
    traceableType: "Donation",
    traceableName: "donation",

    //fields for excel uploader
    modelName: "Offline Gift",
    tempRecords: [],
    modelTemplate: {
      f_name: {
        type: "string",
        value: "",
        required: false
      },
      l_name: {
        type: "string",
        value: "",
        required: false
      },
      email: {
        type: "string",
        value: "",
        required: false
      },
      amount: {
        type: "money",
        value: 0,
        required: true
      },
      designation: {
        type: "select",
        options: [],
        value: ""
      },
      paymentType: {
        type: "select",
        options: OfflineDonationTypes,
        value: "Check"
      },
      date: {
        type: "date",
        value: new Date()
      },
      notes: {
        type: "string",
        value: "",
        required: false
      }
    },

    editing: false,
    meta: {
      page: 1,
      pages: 1
    },
    filter: {
      sort: {
        key: "created_at",
        order: "desc"
      },
      search: {
        key: "user_email_cont", // Ransack search query, add more here to expand the search results
        term: null
      },
      filters: { // Dropdown select filters, need to instantiate initial value
        status: '',
        payment_method: '',
        paid_at: ''
      },
      dateRange: null // 'today', 'this_week', or null for all
    },
    permissions: {},

  }),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.donationService = new DonationService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },
    async loadData() {
      try {
        this.donations = [];
        this.page = 1;
        this.meta = {
          page: 1,
          pages: 1
        };
        await this.getDonations();
      } catch (e) {
        console.error("Error loading data", e);
      }
    },


    getDonations() {
      this.loading = true;
      let params = new URLSearchParams();

      // Apply date range filter
      if (this.filter.dateRange) {
        const now = new Date();
        let startDate;
        if (this.filter.dateRange === 'today') {
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (this.filter.dateRange === 'this_week') {
          const dayOfWeek = now.getDay();
          const monday = new Date(now);
          monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
          startDate = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
        }
        if (startDate) {
          const dateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
          params.set('query[payments_paid_at_gteq]', dateStr);
        }
      }

      this.donationService.paginate(this.meta.page, params, this.filter)
        .then((res) => {
          this.donations = res.data;
          this.meta = res.meta;
          this.permissions = res.permissions;
          this.loading = false;
        }).catch((error) => {
        console.warn('error getting donations', error);
        this.loading = false;
      });
    },

    getDonation(id) {
      this.loadingDonation = true;
      this.editing = false;
      this.donationError = false;

      this.donationService.show(id)
        .then((res) => {
          this.currentDonation = res?.record || null;
          this.permissions = {...this.permissions, ...(res?.permissions || {})};
          this.givingBlind = res?.giving_blind || false;
          this.loadingDonation = false;
          if (!this.currentDonation) this.donationError = true;
        })
        .catch(() => {
          this.currentDonation = null;
          this.loadingDonation = false;
          this.donationError = true;
        });
    },

    // Toggle modal showing and load data;
    async toggleOfflineModal() {
      this.showOfflineModal = !this.showOfflineModal;
      if (this.showOfflineModal) {
        await this.loadDonationTargets();
      }
    },

    async searchDonors(term) {
      return this.donationService.searchDonors(term);
    },

    async loadDonationTargets() {

      try {
        const res = await this.organizationService.getDonationTargets(this.organizationId, true, "Form");
        this.donationTargets = res.json;
        // Quick sort for targets on gifts page, org default first, then funds, then campaigns
        this.donationTargets.sort((a, b) => {
          if (a.org_default && !b.org_default) return -1;
          if (!a.org_default && b.org_default) return 1;
          if (a.org_default && b.org_default) return a.name.localeCompare(b.name);

          if (a.model_identifier === "Fund" && b.model_identifier !== "Fund") return -1;
          if (a.model_identifier !== "Fund" && b.model_identifier === "Fund") return 1;
          if (a.model_identifier === "Fund" && b.model_identifier === "Fund") return a.name.localeCompare(b.name);

          // Priority 3: Everything else, sorted alphabetically
          return a.name.localeCompare(b.name);
        });
        // Add to model template for offline donations
        this.modelTemplate['designation']['options'] = this.donationTargets.map((target) => {
          return target.name;
        });
        this.targetsLoaded = true;
      } catch (error) {
        console.error("Error loading funds and campaigns", error);
      }
    },

    async saveOfflineDonation(donation) {
      donation.amount = Math.round(donation.amount * 100);
      donation.donatable_id = donation.donatable.id;
      donation.donatable_type = donation.donatable.model;
      let params = {
        "organization_id": this.organizationId,
        "donation": donation,
      };

      const res = await this.donationService.offlineDonation(params);

      setTimeout(() => {
        this.showOfflineModal = false;
      }, 1500);

      if (res && res.success) {
        await this.getDonations();
        return true;
      }
      return false;
    },

    async toggleEdit() {
      if (this.editing) {
        this.editing = false;
      } else {
        this.loadingDonation = true;
        await this.loadDonationTargets();
        this.loadingDonation = false;
        this.localDonation = {...this.currentDonation};
        this.localDonation.donatable = this.donationTargets.find(t => t.uuid === this.localDonation.donatable?.uuid); // Need this to get the dropdown to populate on load
        this.editing = true;
      }
    },

    async saveDonation() {
      this.loadingDonation = true;
      this.localDonation.donatable_id = this.localDonation.donatable.id;
      this.localDonation.donatable_type = this.localDonation.donatable.model;
      let params = {
        one_time_donation: {
          ...this.localDonation,
        }
      };
      await this.donationService.update(this.currentDonation.id, params);
      await this.getDonation(this.currentDonation.id);
      this.getDonations();
    },

    resendDonationReceipt(donation) {
      this.donationService = new DonationService(donation.organization_id);
      return this.donationService.resendDonationReceipt(donation.id).then((res) => {
        return true;
      });
    },

    //** EXCEL IMPORTER METHODS
    downloadTemplate() {
      this.donationService.downloadDonationTemplate();
    },

    //** Validated records entered from excel file
    recordValid(record) {
      return record.amount
        && record.designation;
    },

    async parseTemplate(file) {
      const data = await ApplicationHelper.excelFileToJson(file);
      let recordList = [];
      if (data) {
        data.forEach(item => {
          // Parse donation date
          let formattedDate = ApplicationHelper.excelDateToJSDate(item['Date of gift']);
          if (!formattedDate) {
            formattedDate = new Date(); // Gift date is today as default;
          }
          // Need to convert the headers from backend to proper format
          let newRecord = {};
          newRecord['f_name'] = item["Donor First Name"];
          newRecord['l_name'] = item["Donor Last Name"];
          newRecord['email'] = item["Donor Email"];
          newRecord['amount'] = item["Amount"];
          newRecord['designation'] = item["Designation"];
          newRecord['paymentType'] = item['Payment Method'];
          newRecord['date'] = formattedDate;
          newRecord['notes'] = item['Notes'];
          if (this.recordValid(newRecord)) {
            recordList.push(newRecord);
          }
        });
      }
      return recordList;
    },

    //** Adds record from form or excel to the staged list of records to be uploaded
    addToTempList(data) {
      // !Important! - incoming record amounts need to be converted to cents before the upload, coming in as integers
      data.amount = Math.round(data.amount * 100); // Must be whole number
      if (!data.f_name && !data.l_name) {
        data.f_name = "Anonymous";
      }
      this.tempRecords.push(data);
    },

    async addRecords() {
      for (let i = 0; i < this.tempRecords.length; i += 20) {
        const batch = this.tempRecords.slice(i, i + 20);
        await Promise.all(batch.map(record => {
          const params = { ...record };
          return this.donationService.offlineDonation(params)
            .then((res) => {
              record.uploaded = true;
              return res;
            });
        }));
      }
      const analyticEventStore = useAnalyticEventStore();
      analyticEventStore.setOrganizationId(this.organizationId);
      if (this.tempRecords.length > 1) {
        analyticEventStore.logEvent({ action: 'offline-gift-bulk-upload' });
      } else {
        analyticEventStore.logEvent({ action: 'offline-gift-submitted' });
      }
      this.getDonations();
      return true;
    },
    // Sets the Ransack sort terms based on the passed key
    // This gets passed to index method to sort the paginated results
    sort(key) {
      if (this.filter.sort.key === key) {
        // If the passed key is the current key, flip the current sort order
        this.filter.sort.order = this.filter.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        // Otherwise set the new key and order by Ascending
        this.filter.sort.key = key;
        this.filter.sort.order = 'asc';
      }

      this.getDonations();
    },

    search(term) {
      this.filter.search.term = term;
      this.meta.page = 1; //this makes it paginate from page 1 not just current page (resets pagination)
      this.getDonations();
    },

    filterRecords(key, value) {
      if (key === 'paid_at') {
        this.setDateFilter(value);
        return;
      }
      this.filter.filters[key] = value;
      if (key === 'payment_method') this.setProcessorFilters(value);

      this.getDonations();
    },

    setDateFilter(value) {
      // Clear filter value so it's not sent as a Ransack _eq param
      this.filter.filters.paid_at = '';
      this.filter.dateRange = null;

      if (value === 'Today') {
        this.filter.dateRange = 'today';
      } else if (value === 'This Week') {
        this.filter.dateRange = 'this_week';
      } else if (value === 'Descending') {
        // Sort by created_at desc — using created_at instead of payments_paid_at
        // because paid_at is NULL for abandoned/pending donations, causing them to
        // float to the top in a DESC sort. created_at provides consistent ordering.
        this.filter.sort.key = 'created_at';
        this.filter.sort.order = 'desc';
      } else if (value === 'Ascending') {
        this.filter.sort.key = 'created_at';
        this.filter.sort.order = 'asc';
      } else {
        // Cleared — reset to default sort
        this.filter.sort.key = 'created_at';
        this.filter.sort.order = 'desc';
      }
      this.meta.page = 1;
      this.getDonations();
    },

    setProcessorFilters(value) {
      let processorType = null;
      switch (value) {
        case 'Offline':
          processorType = "Processor::OfflineProcessor";
          break;
        case 'Stripe':
          processorType = "Processor::StripeProcessor";
          break;
        case 'Helcim':
          processorType = "Processor::HelcimProcessor";
          break;
      }
      this.filter.filters['payments_payment_processor_type'] = processorType;
    },

    nextPage() {
      if (this.loading) return;
      this.meta.page++;
      this.getDonations();
    },
    prevPage() {
      if (this.loading) return;
      this.meta.page--;
      this.getDonations();
    },
    moveToPage(page) {
      if (this.loading) return;
      this.meta.page = page;
      this.getDonations();
    }
  },

  getters: {

    /**
     * Method used in generic Dashboard Index component.
     * Returns an object containing the index table data.
     * @param state
     */
    getIndexTableData(state) {
      let labels = [
        // {label: 'Name', key: 'name', sort: true},
        {label: 'User', key: 'email', ransackKey: 'user_email', sort: true},
        {label: 'Amount', key: 'amount', format: 'money', sort: true},
        {label: 'Designation', key: 'target_name', sort: false},
        {
          label: 'Status',
          key: 'status',
          format: 'tags',
          sort: false,
          select: true,
          options: ['completed', 'created', 'abandoned', 'error']
        },

        {
          label: 'Payment Method', key: 'payment_method', sort: false,
          select: true,
          options: ['Offline', 'Helcim', 'Stripe']
        },
        {
          label: 'Date Paid',
          key: 'paid_at',
          ransackKey: "payments_paid_at",
          format: 'date',
          sort: false,
          select: true,
          options: ['Today', 'This Week', 'Descending', 'Ascending']
        },
      ];

      // Splice in the org field if the current org has any children.
      // So it only shows for multi level orgs
      if(state.organization?.child_ids?.length) {
        labels.splice(2, 0, {label: "Organization", key: 'organization_name', sort: true, ransackKey: "organization_name"});
      }
      if (state.donations.length > 0) {
        let donations = state.donations.map((donation) => {
          return {
            ...donation,
            email: donation.user?.email,
            payment_method: ApplicationHelper.formatPaymentProcessorType(donation.processor_type),
            status: [donation.status?.toUpperCase()]
          };
        });
        return {
          labels: labels,
          rows: donations
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
  }
});
